import React, { useContext, useState } from "react";
import { CloudUpload, Delete, InsertDriveFileOutlined } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, IconButton, LinearProgress, List, ListItem, ListItemText, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { noop, uniqBy } from "lodash";

import {
  ConversionStage,
  convertFilesToGeoJson,
  findMissingSidecarExtensions,
  GdalConversionError,
  GdalLoadError,
  GEOSPATIAL_FILE_ACCEPT,
  GEOSPATIAL_FILE_CAPTION,
  groupFilesByBasename,
  loadGdal,
  selectPolygons,
  toPolygonBoundary,
} from "fond/gis";
import { getFlattenedFilesFromEvent } from "fond/utils/files";
import { Dropzone, Modal } from "fond/widgets";

import { MapContext } from "../MapProvider";

import { IButtonProps } from "./Toolbar";
import ToolbarButton from "./ToolbarButton";

const UNREADABLE_FILE_ERROR = {
  title: "Could not read file",
  message: "Check the file is a valid Shapefile, TAB, KML or GeoJSON and try again.",
};

const CONVERTER_UNAVAILABLE_ERROR = {
  title: "Could not load file converter",
  message: "Check your connection and try again.",
};

type UploadStatus = { type: "idle" } | { type: "converting"; stage: ConversionStage } | { type: "error"; title: string; message: string };

const STAGE_MESSAGE: Record<ConversionStage, string> = {
  "loading-converter": "Preparing file converter…",
  converting: "Converting…",
};

const UploadPolygonButton: React.FC<IButtonProps> = ({ icon, title = "Upload polygon", ...rest }) => {
  const [showModal, setShowModal] = useState(false);
  const [status, setStatus] = useState<UploadStatus>({ type: "idle" });
  const [files, setFiles] = useState<File[]>([]);
  const { map, drawControl } = useContext(MapContext);

  const isConverting = status.type === "converting";

  const handleOnClick = () => {
    // Switch back to feature select mode.
    drawControl.current?.changeMode("simple_select");
    map?.fire("draw.modechange", { type: "draw.modechange", mode: "simple_select" });

    // Start fetching the converter now so it is ready by the time a file is chosen.
    // Any failure is reported when the conversion itself is attempted.
    loadGdal().catch(noop);

    setShowModal(true);
  };

  const handleOnClose = () => {
    setShowModal(false);
    setStatus({ type: "idle" });
    setFiles([]);
  };

  const importFiles = async (selectedFiles: File[]) => {
    try {
      const collections = await convertFilesToGeoJson(selectedFiles, {
        onStageChange: (stage) => setStatus({ type: "converting", stage }),
      });

      const polygons = selectPolygons(collections);
      if (polygons.length === 0) {
        setStatus({
          type: "error",
          title: "No polygons found",
          message: "The selected file does not contain any valid Multi/Polygons.",
        });
        return;
      }

      const boundary = toPolygonBoundary(polygons);
      if (!boundary) {
        setStatus({ type: "error", ...UNREADABLE_FILE_ERROR });
        return;
      }

      handleOnClose();

      // Fire the drawing event that initiates the auto carve process.
      map?.fire("draw.passing-create", { type: "draw.modechange", features: [boundary] });
    } catch (error) {
      console.error("Polygon upload failed", error, error instanceof GdalConversionError ? error.context : undefined);
      Sentry.captureException(error, { extra: error instanceof GdalConversionError ? { ...error.context, detail: error.detail } : undefined });
      setStatus({ type: "error", ...(error instanceof GdalLoadError ? CONVERTER_UNAVAILABLE_ERROR : UNREADABLE_FILE_ERROR) });
    }
  };

  const handleOnDrop = (droppedFiles: File[]) => {
    if (isConverting) return;

    // Many geospatial formats are multi-file, so we don't want to start importing until the user
    // has dropped _all_ the files for a given dataset.
    const selectedFiles = uniqBy([...droppedFiles, ...files], (file) => file.name.toLowerCase());
    setFiles(selectedFiles);
    setStatus({ type: "idle" });

    const bundles = Object.values(groupFilesByBasename(selectedFiles));
    if (bundles.every((bundle) => findMissingSidecarExtensions(bundle).length === 0)) {
      importFiles(selectedFiles).catch(noop);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles(files.filter((file) => file !== fileToRemove));
    setStatus({ type: "idle" });
  };

  return (
    <>
      <ToolbarButton title={title} {...rest} onClick={handleOnClick}>
        {icon || <CloudUpload />}
      </ToolbarButton>

      {showModal && (
        <Modal
          open
          variant="default"
          header="Upload polygon"
          headerIcon={<CloudUpload />}
          data-testid="upload-polygon-modal"
          content={
            <>
              {status.type === "error" && (
                <Alert severity="error" sx={{ my: 1 }}>
                  <AlertTitle>{status.title}</AlertTitle>
                  {status.message}
                </Alert>
              )}

              <Dropzone
                onDropAccepted={handleOnDrop}
                acceptedFiles={GEOSPATIAL_FILE_ACCEPT}
                getFilesFromEvent={getFlattenedFilesFromEvent}
                disabled={isConverting}
                multiple
              >
                <Box display="flex" flexDirection="column" alignItems="center" width="100%" py={4} borderRadius="4px">
                  <CloudUpload />
                  <Typography variant="caption" sx={{ color: (theme) => theme.palette.biarri.primary.darkGrey }}>
                    Drag and drop files or click to browse
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(101, 101, 101, 0.80)" }}>
                    {GEOSPATIAL_FILE_CAPTION}
                  </Typography>
                </Box>
              </Dropzone>

              {status.type === "converting" && (
                <Box my={2}>
                  <Typography variant="caption">{STAGE_MESSAGE[status.stage]}</Typography>
                  <LinearProgress />
                </Box>
              )}

              {files.length > 0 && !isConverting && (
                <List dense disablePadding sx={{ mt: 1 }}>
                  {Object.entries(groupFilesByBasename(files)).map(([name, bundle]) => {
                    const missing = findMissingSidecarExtensions(bundle);
                    return (
                      <Box key={name}>
                        {bundle.map((file) => (
                          <ListItem
                            key={file.name}
                            disableGutters
                            secondaryAction={
                              <IconButton edge="end" aria-label={`Remove ${file.name}`} onClick={() => handleRemoveFile(file)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            }
                          >
                            <InsertDriveFileOutlined color="primary" fontSize="small" sx={{ mr: 1 }} />
                            <ListItemText primaryTypographyProps={{ variant: "caption", noWrap: true }} primary={file.name} />
                          </ListItem>
                        ))}
                        {missing.length > 0 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>
                            Missing: {missing.join(", ")}
                          </Alert>
                        )}
                      </Box>
                    );
                  })}
                </List>
              )}
            </>
          }
          actions={
            <Button color="primary" onClick={handleOnClose}>
              Close
            </Button>
          }
        />
      )}
    </>
  );
};

export default UploadPolygonButton;
